import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { LayoutService } from '../../core/layout/layout.service';
import { SidebarComponent } from '../sidebar/sidebar.component';

const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The sm-band overlay drawer (FR-024/FR-025). Focus is contained by a
 * hand-rolled Tab/Shift+Tab cycle — the documented R-009 fallback after the
 * D9 budget gate rejected @angular/cdk's cdkTrapFocus (initial chunk exceeded
 * 300 kB raw). Escape, scrim activation, and navigation each close it; focus
 * returns to the control that opened it. Hosted by the shell via @if, so
 * closing removes it from the DOM — no orphaned scrim.
 */
@Component({
  selector: 'app-sidebar-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SidebarComponent],
  template: `
    <div class="sidebar-drawer-scrim" (click)="close()" aria-hidden="true"></div>
    <div
      class="sidebar-drawer"
      [class.reduced-motion]="layout.prefersReducedMotion()"
      role="dialog"
      aria-modal="true"
      aria-label="Navigation"
      tabindex="-1"
      (keydown.escape)="close()"
      (keydown)="trapTab($event)"
    >
      <app-sidebar />
    </div>
  `,
  styleUrl: './sidebar-drawer.component.scss',
})
export class SidebarDrawerComponent implements OnDestroy {
  protected readonly layout = inject(LayoutService);

  /** The element that had focus when the drawer opened — restored on close. */
  private readonly opener: HTMLElement | null;
  private readonly host: ElementRef<HTMLElement>;

  constructor() {
    this.host = inject<ElementRef<HTMLElement>>(ElementRef);
    this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Close on navigation (Edge Cases): the destination link was activated.
    inject(Router)
      .events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe(() => this.close());

    // Move focus inside on open (initial trap entry).
    afterNextRender(() => {
      const drawer = this.drawerEl();
      if (!drawer) return;
      const first = this.tabbable()[0];
      (first ?? drawer).focus();
    });
  }

  /** Hand-rolled focus trap: wrap Tab/Shift+Tab at the drawer's edges. */
  protected trapTab(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const tabbable = this.tabbable();
    if (tabbable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = tabbable[0];
    const last = tabbable[tabbable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  close(): void {
    this.layout.closeDrawer();
  }

  ngOnDestroy(): void {
    // Return focus to the control that opened the drawer (FR-025).
    if (this.opener && document.contains(this.opener)) {
      this.opener.focus();
    }
  }

  private drawerEl(): HTMLElement | null {
    return this.host.nativeElement.querySelector('.sidebar-drawer');
  }

  private tabbable(): HTMLElement[] {
    const drawer = this.drawerEl();
    if (!drawer) return [];
    return Array.from(drawer.querySelectorAll<HTMLElement>(TABBABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }
}
